import Check from '@mui/icons-material/Check';
import Clear from '@mui/icons-material/Clear';
import Info from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import owasp from 'owasp-password-strength-test';
import { type ReactNode, useEffect, useState } from 'react';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme, errorColor, successColor, warningColor } from '../../provider/ThemeProvider';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import { type InputTextProps } from './Text';
import InputNonEditValue from './helpers/render-non-edit-value';

owasp.config({ allowPassphrases: true, minLength: 8 });

export type InputPasswordProps = {
    autoWidth?: boolean;
    autoComplete?: string;
    blink?: boolean;
    callback?: (params: { id: string; val: string }) => void;
    className?: string;
    disabled?: boolean;
    edit?: boolean;
    endIcon?: IconifyProps;
    error?: string;
    fatLabel?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    hideLabelEdit?: boolean;
    icon?: IconifyProps;
    id?: string;
    info?: string;
    l?: string;
    loading?: boolean;
    onKeyUp?: () => void;
    passCheck?: boolean;
    showPassTooltip?: boolean;
    testid?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    type?: 'password';
    v?: string;
    vNew?: string;
};

export function convertPasswordValue(v: InputPasswordProps['v'], passCheck: InputPasswordProps['passCheck'], showPassTooltip: InputPasswordProps['showPassTooltip']): ReactNode {
    if (passCheck) {
        if (!checkUndefinedNullOrEmpty(v)) {
            const servPass = owasp.test(v);
            const passCheckered: any = {};
            passCheckered.password = v;
            passCheckered.passedTests = servPass.passedTests;
            passCheckered.score = servPass.passedTests.length > 6 ? 4 : servPass.passedTests.length > 5 ? 3 : servPass.passedTests.length > 4 ? 2 : servPass.passedTests.length > 3 ? 1 : 0;
            passCheckered.hasLength = v.length > 7;
            passCheckered.hasNumber = !checkUndefinedNullOrEmpty(v.match(/([0-9])/g));
            passCheckered.hasSonder = !checkUndefinedNullOrEmpty(v.match(/\W/g));

            return showPassTooltip ? (
                <Tooltip
                    key="password"
                    sx={{ whiteSpace: 'pre' }}
                    title={
                        v +
                        '\n' +
                        (passCheckered.score === 4
                            ? '\nSehr starkes Passwort 😊'
                            : passCheckered.score === 3
                              ? '\nStarkes Passwort 🙂'
                              : passCheckered.score === 2
                                ? '\nMittelmäßiges Passwort 😕'
                                : passCheckered.score === 1
                                  ? '\nSchwaches Passwort 🙁'
                                  : '\nSehr schwaches Passwort ☹️') +
                        (!passCheckered.hasLength ? '\nPasswort kürzer als 8 Zeichen!' : '') +
                        (!passCheckered.hasNumber ? '\nEine Zahl fehlt!' : '') +
                        (!passCheckered.hasSonder ? '\nEin Sonderzeichen fehlt!' : '')
                    }
                >
                    <span style={{ color: passCheckered.score === 4 || passCheckered.score === 3 ? successColor : passCheckered.score === 2 ? warningColor : errorColor }}>
                        {passCheckered.score === 4 ? '*****' : passCheckered.score === 3 ? '****' : passCheckered.score === 2 ? '***' : passCheckered.score === 1 ? '**' : '*'}
                    </span>
                </Tooltip>
            ) : passCheckered.score === 4 ? (
                '*****'
            ) : passCheckered.score === 3 ? (
                '****'
            ) : passCheckered.score === 2 ? (
                '***'
            ) : passCheckered.score === 1 ? (
                '**'
            ) : (
                '*'
            );
        }
        return '-';
    } else {
        return !checkUndefinedNullOrEmpty(v) ? (
            showPassTooltip ? (
                <Tooltip key="password" title={v}>
                    <span>{v.replace(/./g, '*')}</span>
                </Tooltip>
            ) : (
                v.replace(/./g, '*')
            )
        ) : (
            '-'
        );
    }
}

const InputPassword = (moduleProps: InputPasswordProps) => {
    const {
        autoWidth,
        autoComplete,
        blink,
        callback,
        disabled,
        edit,
        endIcon,
        error,
        fatLabel,
        helperText,
        hideLabel,
        hideLabelEdit,
        icon,
        id,
        info,
        l,
        loading,
        onKeyUp,
        passCheck,
        showPassTooltip,
        testid,
        textAlign,
        v,
        vNew,
    } = {
        autoWidth: false,
        blink: false,
        disabled: false,
        edit: false,
        fatLabel: false,
        hideLabel: false,
        hideLabelEdit: false,
        loading: false,
        passCheck: false,
        showPassTooltip: true,
        ...moduleProps,
    };
    const [activeInput, setActiveInput] = useState<boolean>(false);
    const [capsLockWarning, setCapsLockWarning] = useState<boolean>(false);
    const [passChecker, setPassChecker] = useState<any>(undefined);
    const [value, setValue] = useState<InputTextProps['v']>(v);
    const theme: CustomTheme = useTheme();

    const getValue = (val) => {
        if (!edit && loading) {
            return <Skeleton animation="wave" width="100%" height="calc(1em + 8px)" />;
        }
        if (edit) {
            if (passCheck) {
                if (!checkUndefinedNullOrEmpty(val)) {
                    const servPass = owasp.test(val);
                    const passCheckered: any = {};
                    passCheckered.password = val;
                    passCheckered.passedTests = servPass.passedTests;
                    passCheckered.score = servPass.passedTests.length > 6 ? 4 : servPass.passedTests.length > 5 ? 3 : servPass.passedTests.length > 4 ? 2 : servPass.passedTests.length > 3 ? 1 : 0;
                    passCheckered.hasLength = val.length > 7;
                    passCheckered.hasNumber = !checkUndefinedNullOrEmpty(val.match(/([0-9])/g));
                    passCheckered.hasSonder = !checkUndefinedNullOrEmpty(val.match(/\W/g));
                    if (!passChecker || (passChecker && val !== passChecker.password)) setPassChecker(passCheckered);

                    return val;
                }
                return '';
            } else {
                return !checkUndefinedNullOrEmpty(val) ? val : '';
            }
        }

        return convertPasswordValue(val, passCheck, showPassTooltip);
    };

    const handleSubmit = (val) => {
        if (callback) callback({ id: id, val: val });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputPassword_' + id);
    };

    const handleChange = (e) => {
        const newValue = e.target.value || '';
        setValue(newValue);
        handleSubmit(newValue);
    };

    const heading = [];
    if (!hideLabel) {
        if (icon) {
            heading.push(
                <span key={'inputPassword_' + id + '_headingIcon'} className="materialIcon iconMarginRight">
                    <Iconify icon={icon} />
                </span>
            );
        }
        if (l) heading.push(l);
    }

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    return edit ? (
        <div
            className={[
                blink ? 'blinkInputfield' : '',
                !checkUndefinedOrNull(passChecker)
                    ? !passChecker.hasLength || !passChecker.hasNumber || !passChecker.hasSonder
                        ? 'passwordRed'
                        : passChecker.score === 4 || passChecker.score === 3
                          ? 'passwordGreen'
                          : passChecker.score === 2
                            ? 'passwordOrange'
                            : 'passwordRed'
                    : '',
                'inputPassword',
            ].join(' ')}
            key={'inputPassword_' + id}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : textAlign === 'justify' ? 'space-between' : 'flex-start',
                width: !autoWidth ? '100%' : undefined,
            }}
        >
            {(loading || icon) && (
                <div className="materialIcon iconMarginRight" style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginTop: 20, marginBottom: -20 }}>
                    {loading ? <CircularProgress color="primary" size="1.5rem" /> : icon ? <Iconify icon={icon} /> : undefined}
                </div>
            )}
            <FormControl fullWidth>
                <TextField
                    id={id}
                    type="password"
                    value={!checkUndefined(value) ? getValue(value) : null}
                    label={!hideLabelEdit ? l : undefined}
                    onChange={handleChange}
                    onKeyUp={(e) => {
                        if (!checkUndefinedOrNull(onKeyUp) && e.key === 'Enter') {
                            e.preventDefault();
                            onKeyUp();
                        }
                    }}
                    autoComplete={autoComplete ? 'section-ahdcars ' + autoComplete : 'off'}
                    error={!checkUndefinedOrNull(error) || !checkUndefinedOrNull(passChecker)}
                    helperText={!checkUndefinedOrNull(error) ? error : helperText}
                    inputProps={{
                        onFocus: () => setActiveInput(true),
                        onBlur: () => setActiveInput(false),
                        onKeyUp: (e) => setCapsLockWarning(e.getModifierState('CapsLock')),
                        placeholder: hideLabelEdit ? l : '',
                        style: { textAlign: textAlign },
                    }}
                    InputLabelProps={{
                        shrink: !checkUndefinedNullOrEmpty(value),
                    }}
                    disabled={loading || disabled}
                />
                {(capsLockWarning || !checkUndefinedOrNull(passChecker)) && activeInput && (
                    <Paper style={{ position: 'absolute', top: 50, padding: 10, width: '100%', zIndex: 10 }}>
                        {capsLockWarning && (
                            <Typography style={{ color: theme?.palette?.error?.main }} variant="body1">
                                FESTSTELLTASTE AKTIVIERT!
                            </Typography>
                        )}
                        {!checkUndefinedOrNull(passChecker) && <Typography variant="h6">Mindestanforderungen</Typography>}
                        {!checkUndefinedOrNull(passChecker) && (
                            <Typography style={{ color: theme.palette[passChecker.hasLength ? 'success' : 'error'].main, display: 'flex' }} variant="body1">
                                {passChecker.hasLength ? <Check style={{ lineHeight: 1, marginRight: 8 }} /> : <Clear style={{ lineHeight: 1, marginRight: 8 }} />}länger als 8 Zeichen
                            </Typography>
                        )}
                        {!checkUndefinedOrNull(passChecker) && (
                            <Typography style={{ color: theme.palette[passChecker.hasNumber ? 'success' : 'error'].main, display: 'flex' }} variant="body1">
                                {passChecker.hasNumber ? <Check style={{ lineHeight: 1, marginRight: 8 }} /> : <Clear style={{ lineHeight: 1, marginRight: 8 }} />}beinhaltet eine Zahl
                            </Typography>
                        )}
                        {!checkUndefinedOrNull(passChecker) && (
                            <Typography style={{ color: theme.palette[passChecker.hasSonder ? 'success' : 'error'].main, display: 'flex' }} variant="body1">
                                {passChecker.hasSonder ? <Check style={{ lineHeight: 1, marginRight: 8 }} /> : <Clear style={{ lineHeight: 1, marginRight: 8 }} />}beinhaltet ein Sonderzeichen
                            </Typography>
                        )}
                        {!checkUndefinedOrNull(passChecker) && (
                            <LinearProgress
                                color="primary"
                                className={passChecker.score === 4 || passChecker.score === 3 ? 'passwordGreen' : passChecker.score === 2 ? 'passwordOrange' : 'passwordRed'}
                                style={{ marginTop: 10 }}
                                variant="determinate"
                                value={passChecker.score === 4 ? 100 : passChecker.score === 3 ? 80 : passChecker.score === 2 ? 60 : passChecker.score === 1 ? 40 : 20}
                            />
                        )}
                        {!checkUndefinedOrNull(passChecker) && (
                            <Typography style={{ fontSize: '0.8em' }} variant="body2">
                                {passChecker.score === 4
                                    ? 'Sehr starkes Passwort 😊'
                                    : passChecker.score === 3
                                      ? 'Starkes Passwort 🙂'
                                      : passChecker.score === 2
                                        ? 'Mittelmäßiges Passwort 😕'
                                        : passChecker.score === 1
                                          ? 'Schwaches Passwort 🙁'
                                          : 'Sehr schwaches Passwort ☹️'}
                            </Typography>
                        )}
                    </Paper>
                )}
            </FormControl>
            {!checkUndefinedOrNull(endIcon) && (
                <div
                    className="materialIcon"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                        marginLeft: 8,
                        marginTop: 20,
                        marginBottom: -20,
                    }}
                >
                    <Iconify icon={endIcon} />
                </div>
            )}
            {!checkUndefinedOrNull(info) && (
                <div className="materialIcon" style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 8, marginTop: 20, marginBottom: -20 }}>
                    <Tooltip title={info}>
                        <span>
                            <Info color="primary" />
                        </span>
                    </Tooltip>
                </div>
            )}
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={l}
            loading={loading}
            textAlign={textAlign}
            value={getValue(v)}
        />
    );
};

export default InputPassword;
